/**
 * Pure model for the ⋯ More menu: what a row is, how rows group, and how a
 * query ranks them. Rendering lives in `ToolbarMoreMenu.tsx`; keeping this
 * half pure is what lets the search be unit-tested without a DOM.
 *
 * Ranking reuses the wiki slash menu's tier table (`scoreSearchCandidate`)
 * so both pickers feel identical: diacritic-insensitive, locale-aware,
 * slug > label prefix > label word > keyword > substring.
 */

import type { LucideIcon } from 'lucide-react'
import type { ComponentType } from 'react'

import { scoreSearchCandidate } from '@/features/wiki/lib/slash-command-search'

/** Menu group, in display order. */
export type MoreMenuGroup = 'text' | 'lists' | 'insert' | 'media'

export const MORE_MENU_GROUP_ORDER: readonly MoreMenuGroup[] = ['text', 'lists', 'insert', 'media']

export interface MoreMenuEntry {
  /** Stable id — doubles as the search slug and the DOM option id suffix. */
  id: string
  /** Already-resolved, localized label. */
  label: string
  /** Optional localized description (wiki insert rows carry one). */
  description?: string
  icon: LucideIcon | ComponentType<{ className?: string }>
  group: MoreMenuGroup
  /** Whether the entry is currently applied (toggle marks / block types). */
  active: boolean
  /** Display-only keyboard hint, e.g. `Mod-B`. */
  shortcut?: string
  keywords: { en: readonly string[]; es: readonly string[] }
  run: () => void
}

/** Rank + filter entries for a query. Empty query → source order, untouched. */
export function searchMoreMenuEntries(
  entries: readonly MoreMenuEntry[],
  query: string,
  locale: string,
): MoreMenuEntry[] {
  if (query.trim().length === 0) return [...entries]
  const scored: Array<{ entry: MoreMenuEntry; score: number; index: number }> = []
  entries.forEach((entry, index) => {
    const score = scoreSearchCandidate({
      slug: entry.id,
      label: entry.label,
      description: entry.description,
      keywords: entry.keywords,
      query,
      locale,
    })
    if (score !== null) scored.push({ entry, score, index })
  })
  scored.sort((a, b) => (a.score !== b.score ? b.score - a.score : a.index - b.index))
  return scored.map((s) => s.entry)
}

/** Group entries preserving source order inside each group; empty groups are omitted. */
export function groupMoreMenuEntries(
  entries: readonly MoreMenuEntry[],
): Array<{ group: MoreMenuGroup; entries: MoreMenuEntry[] }> {
  return MORE_MENU_GROUP_ORDER.map((group) => ({
    group,
    entries: entries.filter((entry) => entry.group === group),
  })).filter((bucket) => bucket.entries.length > 0)
}

/**
 * Render a TipTap-style shortcut (`Mod-Shift-B`) as glyphs for the
 * current platform. `Mod` is ⌘ on macOS and Ctrl elsewhere. Symbols only —
 * nothing here is translated, which is why the catalog has no key for it.
 */
export function formatShortcut(shortcut: string, isMac: boolean): string {
  return shortcut
    .split('-')
    .map((part) => {
      switch (part) {
        case 'Mod':
          return isMac ? '⌘' : 'Ctrl'
        case 'Shift':
          return isMac ? '⇧' : 'Shift'
        case 'Alt':
          return isMac ? '⌥' : 'Alt'
        default:
          return part.length === 1 ? part.toUpperCase() : part
      }
    })
    .join(isMac ? '' : '+')
}
